/**
 * Phase 76u (Feb-2026) — Small grey pill showing the current app version
 * (APK version_name + build code if available, server version as fallback).
 *
 * Why?
 *   When a user reports "this page is broken", support needs to know which
 *   build they're on. Without this pill, the only way is to ask them to
 *   log out and screenshot the login page. This component fetches
 *   /api/app/latest-release on mount and renders e.g. `v2.0.6 (26)` in
 *   the corner of admin + pilot page headers.
 *
 * Render: <VersionPill /> — no props. Self-contained.
 *
 * It silently shows nothing if both API calls fail (e.g. the user is on
 * a stale offline cache) so it never breaks the layout.
 */
import React, { useEffect, useState } from 'react';
import api from '../lib/api';

export default function VersionPill({ className = '' }) {
  const [label, setLabel] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rel, srv] = await Promise.all([
          api.get('/app/latest-release').catch(() => ({ data: null })),
          api.get('/app/version').catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        let text = '';
        if (rel.data) {
          text = `v${rel.data.version_name} (${rel.data.version_code})`;
        } else if (srv.data) {
          text = `v${srv.data.version}`;
        }
        setLabel(text);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!label) return null;
  return (
    <span
      data-testid="version-pill"
      className={`inline-flex items-center text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-[#F3F3F3] text-[#666] rounded-full ${className}`}
      title="Current app build"
    >
      {label}
    </span>
  );
}
